import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import { fetchTournaments, forceCompleteSimulation, triggerPrizeSettlement, createTournamentsFromPGATour, fetchScorecardSlashgolf, processAllSkinsAndSawOffs } from '../../components/admin/adminApiService';
import tournamentService from '../../services/tournamentService';
import PGATourFetchModal from '../../components/admin/modals/PGATourFetchModal';
import SlashGolfImportModal from '../../components/admin/modals/SlashGolfImportModal';
import CreateTournamentModal from '../../components/admin/modals/CreateTournamentModal';
import PrizeV2Modal from '../../components/admin/modals/PrizeV2Modal';
import toast from 'react-hot-toast';

const AdminTournaments = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  // Add simulation method state
  const [simulationMethod, setSimulationMethod] = useState('instant');
  // Add PGA import modal state
  const [showPGAFetchModal, setShowPGAFetchModal] = useState(false);
  const [isCreatingTournaments, setIsCreatingTournaments] = useState(false);
  
  // Add SlashGolf import modal state
  const [showSlashGolfModal, setShowSlashGolfModal] = useState(false);
  const [isImportingSlashGolf, setIsImportingSlashGolf] = useState(false);
  
  // Add create tournament modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [tournamentForm, setTournamentForm] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    maxPlayers: '',
    status: 'DRAFT',
    rules: ''
  });
  const [createTournamentMutation, setCreateTournamentMutation] = useState({ isLoading: false });

  // Add Prize V2 modal state
  const [showPrizeV2Modal, setShowPrizeV2Modal] = useState(false);
  const [prizeV2Data, setPrizeV2Data] = useState(null);

  // Load tournaments data function
  const loadTournaments = async () => {
    try {
      setLoading(true);
      const data = await fetchTournaments();
      setTournaments(data || []);
    } catch (error) {
      console.error('Error loading tournaments:', error);
      toast.error('Failed to load tournaments');
      setTournaments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTournaments();
  }, []);

  const filteredTournaments = tournaments.filter(tournament => {
    const matchesSearch = tournament.name.toLowerCase().includes(searchTerm.toLowerCase());
    // Convert status to lowercase for filtering
    const statusLower = tournament.status ? tournament.status.toLowerCase() : '';
    const matchesStatus = filterStatus === 'all' || statusLower === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    const statusLower = status ? status.toLowerCase() : '';
    switch (statusLower) {
      case 'active': return '#059669';
      case 'upcoming': return '#d97706';
      case 'completed': return '#6b7280';
      case 'pending': return '#d97706';
      default: return '#6b7280';
    }
  };

  const formatStatus = (status) => {
    if (!status) return 'Unknown';
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Update simulation handler to use selected method
  const handleForceSimulation = async (tournamentId) => {
    const methodText = simulationMethod === 'instant' ? 'instant' : 'hole-by-hole';
    const methodDescription = simulationMethod === 'instant' 
      ? '• Generate all scores immediately\n• Complete tournament instantly'
      : '• Generate scores progressively (10-second intervals)\n• Show live hole-by-hole updates\n• Complete tournament in ~12 minutes';
    
    if (window.confirm(`Are you sure you want to force complete the simulation using ${methodText} method?\n\nThis will:\n${methodDescription}\n• Mark the tournament as completed\n• Trigger automatic prize settlement\n\nThis action cannot be undone.`)) {
      try {
        await forceCompleteSimulation(tournamentId, simulationMethod);
        toast.success(`Simulation completed successfully using ${methodText} method!`);
        // Reload tournaments to get updated status
        const data = await fetchTournaments();
        setTournaments(data || []);
      } catch (error) {
        console.error('Error completing simulation:', error);
        toast.error('Failed to complete simulation');
      }
    }
  };

  const handleTriggerPrizeSettlement = async (tournamentId) => {
    try {
      await triggerPrizeSettlement(tournamentId);
      toast.success('Prize settlement triggered successfully');
      // Reload tournaments to get updated status
      const data = await fetchTournaments();
      setTournaments(data || []);
    } catch (error) {
      console.error('Error triggering prize settlement:', error);
      toast.error('Failed to trigger prize settlement');
    }
  };

  // Handle Prize V2 calculation
  const handlePrizeV2Click = async (tournamentId) => {
    try {
      const result = await tournamentService.calculatePrizeDistributionV2(tournamentId);
      console.log('Prize Distribution V2 Result:', result);
      
      // Show modal with prize data
      if (result.success && result.data) {
        setPrizeV2Data(result.data);
        setShowPrizeV2Modal(true);
        toast.success('Prize V2 calculation complete!');
      } else {
        toast.error(result.message || 'Failed to calculate Prize V2');
      }
    } catch (error) {
      console.error('Error calculating Prize V2:', error);
      toast.error('Failed to calculate Prize V2');
    }
  };

  const handleFetchScorecard = async (tournamentId, tournamentName) => {
    if (window.confirm(`Are you sure you want to fetch scorecard data for "${tournamentName}"?\n\nThis will retrieve live scoring data from SlashGolf API for all players in the tournament.`)) {
      try {
        // Show loading toast
        const loadingToast = toast.loading('Fetching scorecard data from SlashGolf API...');
        
        const result = await fetchScorecardSlashgolf(tournamentId);
        
        // Dismiss loading toast
        toast.dismiss(loadingToast);
        
        if (result.success) {
          const data = result.data;
          const successMessage = `✅ Scorecard fetch completed!\n` +
            `📊 Processed: ${data.processedPlayers}/${data.totalPlayers} players\n` +
            `✅ Successful: ${data.successfulPlayers}\n` +
            `❌ Failed: ${data.failedPlayers}\n` +
            `⏱️ Duration: ${Math.round((new Date(data.endTime) - new Date(data.startTime)) / 1000)}s`;
          
          toast.success(successMessage, { duration: 8000 });
          
          // Show detailed errors if any
          if (data.errors && data.errors.length > 0) {
            const errorDetails = data.errors.map(err => `${err.playerName}: ${err.error}`).join('\n');
            toast.error(`Some players failed to process:\n${errorDetails}`, { duration: 10000 });
          }
        } else {
          toast.error(result.message || 'Failed to fetch scorecard data');
        }
      } catch (error) {
        console.error('Error fetching scorecard:', error);
        toast.error(error.message || 'Failed to fetch scorecard data');
      }
    }
  };

  const handleProcessSkinsAndSawOffs = async (tournamentId, tournamentName) => {
    if (window.confirm(`Are you sure you want to process skins and saw-offs for "${tournamentName}"?\n\nThis will:\n• Find all holes with more than 2 players who have scored\n• Process skin calculations for each qualifying hole\n• Create/update hole events progressively\n• Update the leaderboard with bypass mode\n\nThis operation may take a few moments.`)) {
      let loadingToast = null;
      try {
        // Show loading toast
        loadingToast = toast.loading('Processing skins and saw-offs...');
        
        const result = await processAllSkinsAndSawOffs(tournamentId);
        
        // Dismiss loading toast
        toast.dismiss(loadingToast);
        
        if (result.success) {
          const data = result.data;
          const successMessage = `🏆 Skins & Saw-offs processed!\n` +
            `📊 Qualifying holes: ${data.totalQualifyingHoles}\n` +
            `✅ Processed: ${data.totalProcessed}\n` +
            `🥇 Skins awarded: ${data.totalSkinsAwarded}\n` +
            `🤝 Saw-offs created: ${data.totalSawOffsCreated}\n` +
            `🔄 Carryovers: ${data.totalCarryovers}\n` +
            `📈 Leaderboard: ${data.leaderboardUpdated ? 'Updated' : 'Not updated'}`;
          
          toast.success(successMessage, { duration: 10000 });
          
          // Refresh tournaments list to show updated data
          await loadTournaments();
          
        } else {
          toast.error(result.message || 'Failed to process skins and saw-offs');
        }
        
      } catch (error) {
        console.error('Error processing skins and saw-offs:', error);
        if (loadingToast) {
          toast.dismiss(loadingToast);
        }
        toast.error('Failed to process skins and saw-offs');
      }
    }
  };

  // Handle creating tournaments from PGA Tour data
  const handleCreateTournamentsFromPGATour = async (selectedTournaments) => {
    setIsCreatingTournaments(true);
    try {
      const results = await createTournamentsFromPGATour(selectedTournaments);
      
      // Process array of results
      const successfulImports = results.filter(r => r.success);
      const failedImports = results.filter(r => !r.success);
      
      if (successfulImports.length > 0) {
        const message = failedImports.length > 0
          ? `Successfully imported ${successfulImports.length} tournament(s). ${failedImports.length} failed.`
          : `Successfully imported ${successfulImports.length} tournament(s) as DRAFT status! Remember to add players to each tournament.`;
        
        toast.success(message);
        setShowPGAFetchModal(false);
        
        // Reload tournaments to show the new ones
        const data = await fetchTournaments();
        setTournaments(data || []);
      } else {
        const errorMessages = failedImports.map(f => f.error).join(', ');
        toast.error(`Failed to import tournaments: ${errorMessages}`);
      }
    } catch (error) {
      console.error('Error creating tournaments from PGA Tour:', error);
      toast.error('Failed to import tournaments from PGA Tour');
    } finally {
      setIsCreatingTournaments(false);
    }
  };

  // Handle importing tournaments from SlashGolf API
  const handleSlashGolfImport = async (result) => {
    setIsImportingSlashGolf(false);
    try {
      if (result.errors && result.errors.length > 0) {
        toast.error(`Import completed with ${result.errors.length} errors`);
        console.error('Import errors:', result.errors);
      } else {
        toast.success(`Successfully imported ${result.imported} tournaments`);
      }
      
      if (result.skipped > 0) {
        toast.info(`Skipped ${result.skipped} existing tournaments`);
      }
      
      // Reload tournaments
      const data = await fetchTournaments();
      setTournaments(data || []);
      
    } catch (error) {
      console.error('Error importing tournaments:', error);
      toast.error('Failed to import tournaments');
    }
  };

  const handleCreateTournament = async (tournamentData) => {
    setCreateTournamentMutation({ isLoading: true });
    try {
      const response = await fetch('/api/tournaments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(tournamentData)
      });

      if (response.ok) {
        toast.success('Tournament created successfully!');
        // Reload tournaments
        const data = await fetchTournaments();
        setTournaments(data || []);
        setShowCreateModal(false);
        resetForm();
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Failed to create tournament');
      }
    } catch (error) {
      console.error('Error creating tournament:', error);
      toast.error('Failed to create tournament');
    } finally {
      setCreateTournamentMutation({ isLoading: false });
    }
  };

  const resetForm = () => {
    setTournamentForm({
      name: '',
      description: '',
      startDate: '',
      endDate: '',
      maxPlayers: '',
      status: 'DRAFT',
      rules: ''
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-tournaments">
          <div className="admin-card">
            <div className="loading-container">
              <div className="admin-loading-spinner">
                <div className="admin-spinner"></div>
              </div>
              <p>Loading tournaments...</p>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-tournaments">
        <div className="admin-card">
          <div className="admin-card-header">
            <h2 className="admin-card-title">Tournament Management</h2>
            <p className="admin-card-subtitle">Manage golf tournaments and events ({tournaments.length} total)</p>
          </div>
        </div>

        {/* Filters */}
        <div className="admin-card">
          <div className="admin-filters">
            <div className="admin-form-group">
              <label className="admin-form-label">Search Tournaments</label>
              <input
                type="text"
                className="admin-form-input"
                placeholder="Search by tournament name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Filter by Status</label>
              <select
                className="admin-form-select"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="upcoming">Upcoming</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            {/* Add simulation method selector */}
            <div className="admin-form-group">
              <label className="admin-form-label">Simulation Method</label>
              <select
                className="admin-form-select"
                value={simulationMethod}
                onChange={(e) => setSimulationMethod(e.target.value)}
                title="Choose simulation method for tournaments"
              >
                <option value="instant">⚡ Instant Simulation</option>
                <option value="hole-by-hole">🏌️ Hole-by-Hole Simulation</option>
              </select>
            </div>
          </div>
        </div>

        {/* Tournaments Table */}
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">Tournaments ({filteredTournaments.length})</h3>
            <div className="admin-card-actions">
              <button 
                className="admin-btn admin-btn-secondary"
                onClick={() => setShowPGAFetchModal(true)}
                style={{ marginRight: '8px' }}
                title="Import tournaments from PGA Tour (tournament data only)"
              >
                📥 PGA Tour Import
              </button>
              <button 
                className="admin-btn admin-btn-secondary"
                onClick={() => setShowSlashGolfModal(true)}
                style={{ marginRight: '8px' }}
                title="Import tournaments from SlashGolf API (includes players)"
              >
                🏌️ SlashGolf Import
              </button>
              <button 
                className="admin-btn admin-btn-primary"
                onClick={() => setShowCreateModal(true)}
              >
                ➕ Create Tournament
              </button>
            </div>
          </div>
          
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Players</th>
                  <th>Purchased Players</th>
                  <th>Prize Pool</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTournaments.map((tournament) => (
                  <tr key={tournament.id}>
                    <td className="tournament-id">{tournament.id.substring(0, 8)}...</td>
                    <td>
                      <div className="admin-tournament-name">
                        {tournament.name}
                      </div>
                    </td>
                    <td>{tournament.startDate ? new Date(tournament.startDate).toLocaleDateString() : 'N/A'}</td>
                    <td>{tournament.endDate ? new Date(tournament.endDate).toLocaleDateString() : 'N/A'}</td>
                    <td>{tournament._count?.players || 0}</td>
                    <td>{tournament._count?.bets || 0}</td>
                    <td>{tournament.potSize ? formatCurrency(tournament.potSize) : 'N/A'}</td>
                    <td>
                      <span 
                        className="admin-badge"
                        style={{ backgroundColor: getStatusColor(tournament.status) }}
                      >
                        {formatStatus(tournament.status)}
                      </span>
                    </td>
                    <td>
                      <div className="admin-actions">
                        <button 
                          className="admin-btn admin-btn-small admin-btn-secondary"
                          onClick={() => navigate(`/admin/tournaments/${tournament.id}/edit`)}
                          title="Edit Tournament"
                        >
                          Edit
                        </button>
                        {(tournament.status === 'ACTIVE' || tournament.status === 'LIVE') && (
                          <button 
                            className="admin-btn admin-btn-small admin-btn-info"
                            onClick={() => handleFetchScorecard(tournament.id, tournament.name)}
                            title="Fetch live scorecard data from SlashGolf API"
                          >
                            📊 Fetch Scorecard
                          </button>
                        )}
                        {(tournament.status === 'ACTIVE' || tournament.status === 'LIVE') && (
                          <button 
                            className="admin-btn admin-btn-small admin-btn-warning"
                            onClick={() => handleProcessSkinsAndSawOffs(tournament.id, tournament.name)}
                            title="Process skins and saw-offs for all qualifying holes and update leaderboard"
                          >
                            🏆 Skins & Saw-off
                          </button>
                        )}
                        <button 
                          className="admin-btn admin-btn-small admin-btn-primary"
                          onClick={() => handleForceSimulation(tournament.id)}
                          title={`Force complete simulation using ${simulationMethod === 'instant' ? 'instant' : 'hole-by-hole'} method`}
                        >
                          {simulationMethod === 'instant' ? '⚡' : '🏌️'} Simulate
                        </button>
                        <button 
                          className="admin-btn admin-btn-small admin-btn-success"
                          onClick={() => handleTriggerPrizeSettlement(tournament.id)}
                          title="Trigger Prize Settlement"
                        >
                          💰 Prizes
                        </button>
                        <button 
                          className="admin-btn admin-btn-small admin-btn-warning"
                          onClick={() => handlePrizeV2Click(tournament.id)}
                          title="Calculate Prize Distribution V2 (Per-Round Logic)"
                        >
                          💰 Prize V2
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>


        {/* PGA Tour Import Modal */}
        <PGATourFetchModal
          showModal={showPGAFetchModal}
          setShowModal={setShowPGAFetchModal}
          onConfirmCreate={handleCreateTournamentsFromPGATour}
          isLoading={isCreatingTournaments}
        />

        {/* Create Tournament Modal */}
        <CreateTournamentModal
          showCreateModal={showCreateModal}
          setShowCreateModal={setShowCreateModal}
          tournamentForm={tournamentForm}
          setTournamentForm={setTournamentForm}
          handleCreateTournament={handleCreateTournament}
          createTournamentMutation={createTournamentMutation}
          resetForm={resetForm}
        />

        {/* SlashGolf Import Modal */}
        <SlashGolfImportModal
          showModal={showSlashGolfModal}
          setShowModal={setShowSlashGolfModal}
          onImportComplete={handleSlashGolfImport}
          isLoading={isImportingSlashGolf}
        />

        {/* Prize V2 Modal */}
        <PrizeV2Modal
          showModal={showPrizeV2Modal}
          setShowModal={setShowPrizeV2Modal}
          prizeData={prizeV2Data}
        />
      </div>
    </AdminLayout>
  );
};

export default AdminTournaments;
